<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import CampaignsAPI from 'dashboard/api/campaigns';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  campaignType: {
    type: String,
    default: 'campaign',
    validator: value => ['campaign', 'whatsapp_campaign'].includes(value),
  },
});

const api = computed(() =>
  props.campaignType === 'whatsapp_campaign' ? WhatsappCampaignsAPI : CampaignsAPI
);

const emit = defineEmits(['close']);

const deliveries = ref([]);
const loading = ref(false);
const selectedDate = ref(formatDate(new Date()));
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const summary = ref({ total_sent: 0, total_failed: 0, total_today: 0 });
const searchPhone = ref('');
const deleting = ref(null);
const dialogRef = ref(null);

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const filteredDeliveries = computed(() => {
  if (!searchPhone.value) return deliveries.value;
  const search = searchPhone.value.toLowerCase();
  const searchDigits = search.replace(/\D/g, '');
  return deliveries.value.filter(d => {
    const phoneMatch = searchDigits && d.phone_number.includes(searchDigits);
    const nameMatch = d.contact_name?.toLowerCase().includes(search);
    return phoneMatch || nameMatch;
  });
});

async function loadDeliveries(page = 1) {
  if (!props.campaign?.id) return;

  loading.value = true;
  try {
    const response = await api.value.getDeliveries(
      props.campaign.id,
      {
        date: selectedDate.value,
        page,
        per_page: 50,
      }
    );
    deliveries.value = response.data.deliveries;
    totalPages.value = response.data.meta.total_pages;
    totalCount.value = response.data.meta.total_count;
    currentPage.value = response.data.meta.current_page;
    summary.value = response.data.summary;
  } catch (error) {
    useAlert('Erro ao carregar relatório');
  } finally {
    loading.value = false;
  }
}

async function deleteDelivery(delivery) {
  if (!props.campaign?.id) return;

  if (
    !confirm(
      `Remover registro de ${delivery.contact_name}?\n\nEste contato receberá a campanha novamente no próximo envio.`
    )
  ) {
    return;
  }
  deleting.value = delivery.id;
  try {
    await api.value.deleteDelivery(props.campaign.id, delivery.id);
    useAlert('Registro removido com sucesso');
    loadDeliveries(currentPage.value);
  } catch (error) {
    useAlert('Erro ao remover registro');
  } finally {
    deleting.value = null;
  }
}

async function clearAllForDate() {
  if (!props.campaign?.id) return;

  if (
    !confirm(
      `Limpar todos os ${totalCount.value} registros de ${formatDateDisplay(selectedDate.value)}?\n\nTodos os contatos receberão a campanha novamente.`
    )
  ) {
    return;
  }
  loading.value = true;
  try {
    const response = await api.value.clearDeliveriesForDate(
      props.campaign.id,
      selectedDate.value
    );
    useAlert(response.data.message);
    loadDeliveries(1);
  } catch (error) {
    useAlert('Erro ao limpar registros');
  } finally {
    loading.value = false;
  }
}

async function exportReport() {
  if (!props.campaign?.id) return;

  try {
    const response = await api.value.exportDeliveries(
      props.campaign.id,
      selectedDate.value
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `relatorio_campanha_${props.campaign.id}_${selectedDate.value}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    useAlert('Erro ao exportar relatório');
  }
}

function getStatusConfig(status) {
  const configs = {
    sent: {
      bg: 'bg-green-500/10',
      text: 'text-green-600 dark:text-green-400',
      border: 'border-green-500/20',
      icon: 'i-lucide-check-circle',
      label: 'Enviado',
    },
    failed: {
      bg: 'bg-red-500/10',
      text: 'text-red-600 dark:text-red-400',
      border: 'border-red-500/20',
      icon: 'i-lucide-x-circle',
      label: 'Falhou',
    },
    excluded: {
      bg: 'bg-gray-500/10',
      text: 'text-gray-600 dark:text-gray-400',
      border: 'border-gray-500/20',
      icon: 'i-lucide-minus-circle',
      label: 'Excluído',
    },
  };
  return configs[status] || configs.excluded;
}

function handleClose() {
  emit('close');
}

watch(
  () => selectedDate.value,
  () => {
    loadDeliveries(1);
  }
);

onMounted(() => {
  if (props.isOpen) {
    selectedDate.value = formatDate(new Date());
    loadDeliveries(1);
    nextTick(() => {
      dialogRef.value?.open();
    });
  }
});

watch(
  () => props.isOpen,
  newVal => {
    if (newVal) {
      selectedDate.value = formatDate(new Date());
      loadDeliveries(1);
      nextTick(() => {
        dialogRef.value?.open();
      });
    } else {
      dialogRef.value?.close();
    }
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="`Relatório de Envios - ${campaign.title}`"
    description="Visualize e gerencie os envios desta campanha. Remova registros para permitir reenvio."
    :show-confirm-button="false"
    cancel-button-label="Fechar"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4 max-h-[70vh] overflow-auto">
      <!-- Filters and Actions -->
      <div
        class="flex flex-wrap gap-3 items-center sticky top-0 bg-n-alpha-3 py-2 z-10"
      >
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-n-slate-11">Data:</label>
          <input
            v-model="selectedDate"
            type="date"
            class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-1 text-n-slate-12 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div class="flex items-center gap-2 flex-1 min-w-[200px]">
          <Input
            v-model="searchPhone"
            placeholder="Buscar por telefone ou nome..."
            class="w-full"
          />
        </div>

        <div class="flex gap-2">
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-download"
            label="Exportar CSV"
            @click="exportReport"
          />

          <Button
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            label="Limpar Dia"
            :disabled="loading || totalCount === 0"
            @click="clearAllForDate"
          />
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-3 gap-3">
        <div
          class="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-xl p-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center"
            >
              <span class="i-lucide-check-circle text-xl text-green-500" />
            </div>
            <div>
              <div
                class="text-2xl font-bold text-green-600 dark:text-green-400"
              >
                {{ summary.total_sent }}
              </div>
              <div
                class="text-xs text-green-700 dark:text-green-500 font-medium"
              >
                Total Enviados
              </div>
            </div>
          </div>
        </div>
        <div
          class="bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20 rounded-xl p-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center"
            >
              <span class="i-lucide-x-circle text-xl text-red-500" />
            </div>
            <div>
              <div class="text-2xl font-bold text-red-600 dark:text-red-400">
                {{ summary.total_failed }}
              </div>
              <div class="text-xs text-red-700 dark:text-red-500 font-medium">
                Total Falharam
              </div>
            </div>
          </div>
        </div>
        <div
          class="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-xl p-4"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center"
            >
              <span class="i-lucide-calendar-check text-xl text-blue-500" />
            </div>
            <div>
              <div class="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {{ summary.total_today }}
              </div>
              <div class="text-xs text-blue-700 dark:text-blue-500 font-medium">
                Enviados Hoje
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Date Info -->
      <div class="flex items-center justify-between px-1">
        <div class="text-sm text-n-slate-11">
          <span class="font-medium">{{ formatDateDisplay(selectedDate) }}</span>
          <span class="mx-2">•</span>
          <span>{{ totalCount }} registros</span>
        </div>
        <div v-if="searchPhone" class="text-sm text-n-slate-10">
          {{ filteredDeliveries.length }} resultados encontrados
        </div>
      </div>

      <!-- Deliveries List -->
      <div class="border border-n-weak rounded-xl overflow-hidden">
        <!-- Loading State -->
        <div v-if="loading" class="flex items-center justify-center py-12">
          <div class="flex flex-col items-center gap-3">
            <span
              class="i-lucide-loader-2 text-3xl text-n-slate-10 animate-spin"
            />
            <span class="text-sm text-n-slate-10">Carregando...</span>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredDeliveries.length === 0"
          class="flex items-center justify-center py-12"
        >
          <div class="flex flex-col items-center gap-3">
            <span class="i-lucide-inbox text-4xl text-n-slate-8" />
            <span class="text-sm text-n-slate-10">Nenhum registro encontrado</span>
          </div>
        </div>

        <!-- Deliveries Grid -->
        <div v-else class="divide-y divide-n-weak">
          <div
            v-for="delivery in filteredDeliveries"
            :key="delivery.id"
            class="flex items-center gap-4 px-4 py-3 hover:bg-n-alpha-1 transition-colors"
          >
            <!-- Status Icon -->
            <div
              class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              :class="[getStatusConfig(delivery.status).bg]"
            >
              <span
                class="text-lg"
                :class="[
                  getStatusConfig(delivery.status).icon,
                  getStatusConfig(delivery.status).text,
                ]"
              />
            </div>

            <!-- Contact Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="font-medium text-n-slate-12 truncate">
                  {{ delivery.contact_name || 'Sem nome' }}
                </span>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-medium border"
                  :class="[
                    getStatusConfig(delivery.status).bg,
                    getStatusConfig(delivery.status).text,
                    getStatusConfig(delivery.status).border,
                  ]"
                >
                  {{ getStatusConfig(delivery.status).label }}
                </span>
              </div>
              <div class="flex items-center gap-3 mt-1">
                <span class="text-sm text-n-slate-11 font-mono">
                  {{ delivery.formatted_phone }}
                </span>
                <span class="text-xs text-n-slate-9">•</span>
                <span class="text-xs text-n-slate-10 flex items-center gap-1">
                  <span class="i-lucide-clock text-xs" />
                  {{ formatTime(delivery.created_at) }}
                </span>
              </div>
              <div
                v-if="delivery.error_message"
                class="mt-1 text-xs text-red-500 flex items-center gap-1"
              >
                <span class="i-lucide-alert-circle text-xs" />
                {{ delivery.error_message }}
              </div>
            </div>

            <!-- Actions -->
            <div class="flex-shrink-0">
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                :disabled="deleting === delivery.id"
                :title="
                  deleting === delivery.id
                    ? 'Removendo...'
                    : 'Remover para reenviar'
                "
                @click="deleteDelivery(delivery)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="totalPages > 1"
        class="flex justify-center items-center gap-3 py-2"
      >
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-chevron-left"
          :disabled="currentPage === 1"
          @click="loadDeliveries(currentPage - 1)"
        />
        <div class="flex items-center gap-1">
          <button
            v-for="page in Math.min(totalPages, 5)"
            :key="page"
            class="w-8 h-8 rounded-lg text-sm font-medium transition-colors"
            :class="[
              currentPage === page
                ? 'bg-blue-500 text-white'
                : 'text-n-slate-11 hover:bg-n-alpha-2',
            ]"
            @click="loadDeliveries(page)"
          >
            {{ page }}
          </button>
          <span v-if="totalPages > 5" class="px-2 text-n-slate-10">...</span>
        </div>
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-chevron-right"
          :disabled="currentPage === totalPages"
          @click="loadDeliveries(currentPage + 1)"
        />
      </div>
    </div>
  </Dialog>
</template>
